/**
 * `/works` —— 「我做过什么」的**唯一落点**(2026-07-24 IA 二次收口 F1-a)。
 *
 * ═══ 它为什么存在 ═════════════════════════════════════════════════════════════
 * 此前「看历史」散在三个 hub 页各一份(海报 730 行里 13 处、视频 365 行里 17 处、分身 393 行里
 * 11 处),每个 hub 同时在回答两个问题:「我要开始做什么」(选择 → 卡墙)和「我做过的怎么样了」
 * (检索 → 列表)。两种意图挤一页,谁都不专心;而 AI记者进来会变成第 4 份。
 * 收口口径:**hub 只回答「能做什么」,这一页回答「做过什么」**。
 *
 * ═══ 跨线分页为什么是「加载更多」而不是页码 ═══════════════════════════════════
 * 三条线是三个独立的服务端分页,没有聚合端点(本轮不动后端)。
 * 各线取前 K 条 → 合并 → 再截前 K,得到的就是**真正的全局前 K**(k 路归并;被截掉的条目
 * 在自己线内已有 K 条比它新,不可能进全局前 K)。所以「加载更多」= 把 K 加大重取,
 * **不是**各线翻页拼接 —— 后者在三线产出速度不均时必漏条。
 * 代价是深翻时重复取前面的数据;`PAGE` 取 24、上限 `MAX_PAGES` 页,超出提示用筛选缩小范围
 * (★不静默截断:说清「还有多少条没显示」)。
 * ★以上只适用于「全部」tab。**选定单条线 = 单一服务端分页**,漏条论不成立,用共享
 * `Pagination` 真翻页(?page= 1-based 上 URL),与全站其余 page/size 列表页一致。
 *
 * ═══ 权限降级 ═════════════════════════════════════════════════════════════════
 * 某条线无模块权限 → 不发请求、tab 不出现、也不报错(useModuleHistoryVisible 门)。
 * ★OEM H1:模块未开通 / 已停用 / 已过期(/me/permissions.historyModules)→ 线照常出现,历史作品可看、
 *   可下载、可删;重新生成 / 继续编辑草稿 / 进编辑详情等新建动作一律不可用,并提示 MODULE_LOCKED_NOTICE。
 * 三条线全无权限 → 整页一句话空态 + 去工作台。
 */
import { maskVendor } from "@/shared/lib/vendorMask";
import { useEffect, useMemo, useState } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useRefreshOnDrain } from "@/shared/hooks/useRefreshOnDrain";
import {
  AlertTriangle,
  Download,
  FilePenLine,
  FolderOpen,
  Loader2,
  Search,
  Star,
  UserRound,
} from "lucide-react";

import { useAuth, useModuleHistoryVisible, useModuleLocked } from "@/contexts/AuthContext";
import { moduleLockedNotice } from "@/shared/lib/forbiddenReason";
import { isTenantManagerPlus } from "@/shared/lib/rbac";
import { useOrgScopeFilter } from "@/shared/hooks/useOrgScopeFilter";
import { useUrlParams } from "@/shared/hooks/useUrlParams";
import { useMutationWithToast } from "@/shared/hooks/useMutationWithToast";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { toast } from "@/shared/hooks/use-toast";
import { PageHeader } from "@/shared/ui/PageHeader";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { CenteredLoader } from "@/shared/ui/QueryState";
import { Pagination } from "@/shared/ui/pagination";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { cn } from "@/shared/lib/utils";
import { OrgScopeFilterBar } from "@/entities/org-department/ui/OrgScopeFilterBar";
import { MediaListSection } from "@/entities/media-asset/ui/MediaListSection";
import { MediaDraftCard } from "@/entities/media-asset/ui/MediaDraftCard";
import { MediaFailedJobCard } from "@/entities/media-asset/ui/MediaFailedJobCard";
import { MediaInProgressCard } from "@/entities/media-asset/ui/MediaInProgressCard";
import { MediaFilterChips } from "@/entities/media-asset/ui/MediaFilterChips";
import { MediaSearchBox } from "@/entities/media-asset/ui/MediaSearchBox";
import { SceneFilterChips } from "@/entities/scene-card/ui/SceneFilterChips";
import {
  parseStatusFilter,
  type MediaStatusFilter,
} from "@/entities/media-asset/lib/mediaStatusFilter";
import { splitInFlight } from "@/entities/work/model/inFlight";
import { WorkCard } from "@/entities/work/ui/WorkCard";
import { reporterSiteService } from "@/features/reporter/api/reporterSiteService";
import { SiteBadgeLine } from "@/features/reporter/ui/SiteBadgeLine";
import { SITE_OPTIONS_KEY } from "@/features/reporter/model/sitePublication";
import { workCreatorName, workOriginLabel, workStatusText } from "@/entities/work/model/cardLabels";
import { BATCH_DOWNLOAD_MAX, clampBatchIds } from "@/entities/media-asset/lib/mediaGrid";
import { posterService } from "@/features/poster/api/posterService";
import { ScheduleListSection } from "@/features/poster/ui/components/dashboard/ScheduleListSection";
import { ImagePreviewLightbox } from "@/features/poster/ui/components/ImagePreviewLightbox";
import { VideoPreviewDialog } from "@/features/video/ui/components/VideoPreviewDialog";
import type { WorkDraft, WorkItem, WorkLineKey } from "@/entities/work/model/types";
import { WORK_LINES, mergeWorks, type WorkLineDef } from "./model/workLines";
import { DH_RETIRED_MODE_NOTICE, isRetiredDhMode } from "@/entities/dh-profile/model/dhLongform";

/** 一屏 24 条 = 作品网格全断点列数(2/3/4/5/6)的公倍数,末行不残缺。 */
const PAGE = 24;
/** 归并深度上限:24×8 = 192 条/线。再深就该用筛选,而不是无限拉。 */
const MAX_PAGES = 8;
/** 三节共用的失效根键(任一变更后整页重取)。 */
const WORKS_KEY = ["works"];

export default function WorksPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user, permissions, permissionsLoading } = useAuth();

  /* ── 权限门:hooks 不能在循环里调,四条线各调一次 ── */
  const posterVisible = useModuleHistoryVisible("ai_poster");
  const videoVisible = useModuleHistoryVisible("ai_video");
  const dhVisible = useModuleHistoryVisible("ai_digital_human");
  const reporterVisible = useModuleHistoryVisible("ai_reporter");
  // OEM H1:未开通 / 停用 / 过期的线 = 只读(看、下载、删可以;新建不行)。
  const lockedByKey: Record<string, boolean> = {
    poster: useModuleLocked("ai_poster"),
    video: useModuleLocked("ai_video"),
    dh: useModuleLocked("ai_digital_human"),
    jizhe: useModuleLocked("ai_reporter"),
  };
  const isLocked = (line: WorkLineKey): boolean => !!lockedByKey[line];
  // OEM 第二批 A7:被服务商暂停(/me/permissions.pausedModules)与未开通分开说。
  const lineModule: Record<string, string> = {
    poster: "ai_poster",
    video: "ai_video",
    dh: "ai_digital_human",
    jizhe: "ai_reporter",
  };
  const lockedNoticeOf = (line: WorkLineKey): string =>
    moduleLockedNotice(lineModule[line] ?? line, permissions?.pausedModules);
  const notifyLocked = (line: WorkLineKey) => toast({ title: lockedNoticeOf(line) });
  // ★1101 测试机:长图智能编排在途时点草稿卡 = 进搭建器改备料 / 顺手点一键出图(另起一单另扣费),
  //   编排回来又盖一次 —— 在途只提示不跳转。卡片每 5 秒刷新,编排好了自动变「编排好了」再点。
  //   视频线 busy 不拦:它的创建页本身就是进度页。
  const notifyPlanBusy = () =>
    toast({
      title: "正在智能编排",
      description: "通常 2–5 分钟。编排好了这张卡会自动变成「编排好了」,再点开继续精调。",
    });
  const visibleByKey: Record<string, boolean> = {
    poster: posterVisible,
    video: videoVisible,
    dh: dhVisible,
    jizhe: reporterVisible,
  };
  const lines = useMemo(
    () => WORK_LINES.filter((l) => l.wired && visibleByKey[l.key]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [posterVisible, videoVisible, dhVisible, reporterVisible]
  );

  /* ── 筛选状态(全部上 URL:回退不丢筛选,链接可分享)── */
  const { sp, update } = useUrlParams();
  const lineParam = sp.get("line");
  const activeLine: WorkLineKey | "all" = lines.some((l) => l.key === lineParam)
    ? (lineParam as WorkLineKey)
    : "all";
  const statusFilter = parseStatusFilter(sp.get("status"));
  // ★scene 过滤只在**选定某条线**时生效(codex review P2):sceneKey 按 hub 分档,「全部」视图
  //   下场景 chips 本就隐藏(§sceneHub)——若这里仍把 URL 的 scene 发给每条线的查询,旧链接
  //   `/works?scene=foo` 或指向已失权限线的书签会让页面静默空掉,且没有可见控件能清掉它。
  //   all 视图直接忽略 scene,与「chips 只在选定线出现」的既定语义对齐。
  const sceneFilter = activeLine === "all" ? null : sp.get("scene") || null;
  const search = sp.get("q") ?? "";
  // 1102「我的收藏」(用户反馈:各仓收藏的东西不好找;收藏按人记,后端 V12 xb_media_favorite)。收藏只在海报/视频/分身三线有(新闻线没有),
  // 新闻 tab 下忽略旧链接带的 fav,免得页面静默空掉又没有可见控件清掉它(同 scene 的处理)。
  const favOnly = sp.get("fav") === "1" && activeLine !== "jizhe";

  const canSwitchScope = isTenantManagerPlus(user?.role);
  const orgFilter = useOrgScopeFilter({ canSwitchScope, resetKeys: [] });
  const { scopeParams } = orgFilter;

  const [pages, setPages] = useState(1);
  const resetDepth = () => setPages(1);

  /* ── 单线真翻页(见文件头★):选定某条线 = 单一服务端分页,页码上 URL(?page= 1-based)。
     「全部」tab 仍走 k 路归并的「加载更多」(pages 深度),两套互不掺和。 ── */
  const singleLine = activeLine !== "all";
  const rawPage = Number.parseInt(sp.get("page") ?? "", 10);
  const pageN = singleLine && Number.isFinite(rawPage) && rawPage > 1 ? rawPage - 1 : 0;
  const changePage = (p0: number) => update({ page: p0 <= 0 ? null : String(p0 + 1) });

  /* ── 预览器:「列表=预览,详情=编辑」是本仓既定分工,hub 收成卡墙后这两个预览器跟着搬过来 ── */
  const [previewPoster, setPreviewPoster] = useState<WorkItem | null>(null);
  const [previewVideoId, setPreviewVideoId] = useState<number | null>(null);

  /* ── 批量打包下载(★海报独有:后端只有 /poster/assets/batch-download 一个 zip 端点)──
     只在选定海报线时可用 —— 跨线多选会做出一个「选了视频却下不出来」的假承诺。 */
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const exitSelect = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const changeLine = (v: WorkLineKey | "all") => {
    // 切线必须清场景筛:sceneKey 是按 hub 分的,海报的场景 key 在视频线上永远筛不出东西。
    // page 同理:线 A 的第 3 页在线 B 上没有意义。
    update({ line: v === "all" ? null : v, scene: null, page: null });
    resetDepth();
    // ★退出多选:批量打包是海报独有(后端只有海报 zip 端点)。不退的话,离开海报线后
    // 已选的 id 还在,再点「打包下载」会把非海报任务的 id 混进去 → 后端 400。
    exitSelect();
  };
  const changeStatus = (v: MediaStatusFilter) => {
    update({ status: v === "ALL" ? null : v, page: null });
    resetDepth();
  };
  const changeScene = (v: string | null) => {
    update({ scene: v, page: null });
    resetDepth();
  };
  const toggleFav = () => {
    update({ fav: favOnly ? null : "1", page: null });
    resetDepth();
  };
  const changeSearch = (v: string) => {
    // 每次按键都改 URL,用 replace 避免历史里堆一串中间态(同三线列表既有做法)。
    update({ q: v || null, page: null }, { replace: true });
    resetDepth();
  };

  const queriedLines: WorkLineDef[] = (
    activeLine === "all" ? lines : lines.filter((l) => l.key === activeLine)
  ).filter((l) => !favOnly || !!l.setFavorite); // 只看收藏:没有收藏概念的线不查

  const size = singleLine ? PAGE : PAGE * pages;
  const listParams = {
    page: pageN,
    size,
    status: statusFilter !== "ALL" ? statusFilter : undefined,
    sceneKey: sceneFilter ?? undefined,
    q: search || undefined,
    favorite: favOnly || undefined,
    ...scopeParams,
  };

  /* ── 作品(终态):每线一个 query,合并后截前 size ──
     ★retry 1 次而不是 0:这一页一次要发 3 条线 × 3 类(历史/在途/草稿)最多 8 个并发请求,
     单个请求的瞬时失败概率被放大 8 倍(测试环境实测:12 并发里必有 1 个连接失败)。
     单线失败会**整条线消失**却看不出少了谁 —— 一次重试挡掉绝大多数抖动,失败仍按线报。 */
  const historyQs = useQueries({
    queries: queriedLines.map((l) => ({
      queryKey: ["works", "history", l.key, listParams],
      queryFn: () => l.listHistory(listParams),
      retry: 1,
      staleTime: 15_000,
      // 翻页/改筛选时保留上一页数据免整节闪 loader(仓内既有惯例);只在**同一条线**内保留 ——
      // useQueries 按下标复用 observer,切 tab 时槽位会换线,跨线保留会把海报条目闪在视频 tab 下。
      placeholderData: (
        prev: { items: WorkItem[]; total: number } | undefined,
        prevQ: { queryKey: readonly unknown[] } | undefined
      ) => (prevQ?.queryKey[2] === l.key ? prev : undefined),
      // 页面自渲染 per-line 错误条(failedLines)→ 退订全局 toast 防双报
      meta: { suppressGlobalErrorToast: true },
    })),
  });

  /* ── 在途:不受状态/场景/搜索筛选影响(筛「成功」再建一条新任务,那条不该从视野消失)── */
  const runningQs = useQueries({
    queries: queriedLines.map((l) => ({
      queryKey: ["works", "running", l.key, scopeParams],
      queryFn: () => l.listRunning(scopeParams),
      retry: 1,
      meta: { suppressGlobalErrorToast: true },
      // ★只为**真在跑**的轮询:近 24h 失败卡也在这份数据里(见 splitInFlight),
      //   拿 length 判会让两张失败卡拖着 5s 轮询转满 24 小时。
      refetchInterval: (q: { state: { data?: WorkItem[] } }) =>
        splitInFlight(q.state.data ?? []).active.length > 0 ? 5000 : false,
    })),
  });

  // ★在途清空时刷新历史(codex review P2):running 轮询在集空时停止,若不 invalidate history,
  //   刚完成的任务从「进行中」消失后要等下次无关 refetch 才进「作品」,中间两列表都看不到它。
  const qc = useQueryClient();
  const runningTotal = runningQs.reduce((n, q) => n + splitInFlight(q.data ?? []).active.length, 0);
  useRefreshOnDrain(runningTotal, () => qc.invalidateQueries({ queryKey: ["works", "history"] }));

  /* ── 草稿:只有海报/视频有;不分页(草稿本来就少),错误静默不阻塞主列表 ── */
  const draftLines = queriedLines.filter((l) => l.listDrafts);
  const draftQs = useQueries({
    queries: draftLines.map((l) => ({
      queryKey: ["works", "drafts", l.key],
      queryFn: () => l.listDrafts!(),
      retry: 1,
      staleTime: 30_000,
      meta: { suppressGlobalErrorToast: true },
      // ★1005:有草稿背后的编排还在跑就轮询 —— 编排完成卡上自动从「编排中」变成「编排好了」,
      //   用户不用手动刷新;全部落定即停。
      refetchInterval: (q: { state: { data?: WorkDraft[] } }) =>
        (q.state.data ?? []).some((d) => d.busy) ? 5000 : false,
    })),
  });

  const merged = mergeWorks(
    historyQs.map((q) => q.data?.items ?? []),
    size
  );
  /* ── 1009 新闻线卡上的「官网」状态:按本页新闻稿 id 批查一次(失败静默,只少一行徽标) ── */
  const jizheIds = merged.filter((w) => w.line === "jizhe").map((w) => w.id);
  const siteBadgesQ = useQuery({
    queryKey: ["reporter-site-badges", jizheIds.join(",")],
    queryFn: () => reporterSiteService.byDrafts(jizheIds),
    enabled: jizheIds.length > 0 && !isLocked("jizhe"),
    staleTime: 30_000,
    meta: { suppressGlobalErrorToast: true },
  });
  const siteOptionsQ = useQuery({
    queryKey: SITE_OPTIONS_KEY,
    queryFn: () => reporterSiteService.sites(),
    enabled: jizheIds.length > 0 && !isLocked("jizhe"),
    staleTime: 60_000,
    meta: { suppressGlobalErrorToast: true },
  });
  const siteBadgeByDraft = new Map(
    (siteBadgesQ.data ?? []).map((b) => [String(b.draftId), b.items] as const)
  );
  const tenantHasSites = (siteOptionsQ.data?.length ?? 0) > 0;
  const totalAll = historyQs.reduce((s, q) => s + (q.data?.total ?? 0), 0);
  const historyLoading = historyQs.some((q) => q.isLoading);
  // ★按线报错:聚合页里「少了一条线」肉眼看不出来 —— 通用错误横幅只说「加载失败」,
  // 用户会以为自己真的没有分身作品。说清是哪条线没加载出来,并给一个只重取那条线的按钮。
  const failedLines = queriedLines.filter((_, i) => historyQs[i]?.isError);
  const { active: running, failed: failedJobs } = splitInFlight(
    runningQs.flatMap((q) => q.data ?? [])
  );
  const drafts = draftQs.flatMap((q) => q.data ?? []);
  const hasMore = !singleLine && merged.length < totalAll && pages < MAX_PAGES;
  const cappedRemaining = !singleLine && pages >= MAX_PAGES ? totalAll - merged.length : 0;
  const historyFetching = historyQs.some((q) => q.isFetching);

  /* ── 翻页越界自愈:删掉末页最后一件 / 收紧筛选 / 旧链接带大 page,当前页会空着但明明有数据。
     总数到位后把 page 收回到实际最后一页。 ── */
  const lastPage = Math.max(0, Math.ceil(totalAll / PAGE) - 1);
  useEffect(() => {
    if (singleLine && !historyFetching && totalAll > 0 && pageN > lastPage) changePage(lastPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleLine, historyFetching, totalAll, pageN, lastPage]);

  /* ── 变更类动作:全部按线分派(注册表里没有的动作,卡上就不出按钮;`!` 成立因为
     按钮只在 def.xxx 存在时渲染 —— 官媒线三动作后端无端点,全不出)── */
  const renameMut = useMutationWithToast({
    mutationFn: ({ item, name }: { item: WorkItem; name: string }) =>
      lineDefOf(item.line).rename!(item.id, name),
    invalidateKeys: [WORKS_KEY],
  });
  const favMut = useMutationWithToast({
    mutationFn: (item: WorkItem) => lineDefOf(item.line).setFavorite!(item.id, !item.isFavorite),
    invalidateKeys: [WORKS_KEY],
  });
  const deleteMut = useMutationWithToast({
    mutationFn: (item: WorkItem) => lineDefOf(item.line).remove!(item.id),
    invalidateKeys: [WORKS_KEY],
    successToast: "已删除",
  });
  const retryMut = useMutationWithToast({
    mutationFn: (item: WorkItem) => lineDefOf(item.line).retry!(item.id),
    invalidateKeys: [WORKS_KEY],
    successToast: "已重新提交生成",
    errorToast: "重试失败",
  });
  const deleteDraftMut = useMutationWithToast({
    mutationFn: ({ line, draftId }: { line: WorkLineKey; draftId: string }) =>
      lineDefOf(line).deleteDraft!(draftId),
    invalidateKeys: [WORKS_KEY],
    successToast: "草稿已删除",
  });

  const batchDownloadMut = useMutationWithToast({
    mutationFn: (ids: number[]) => posterService.batchDownload(ids),
    successToast: "已开始打包下载",
    errorToast: "打包下载失败",
    onSuccess: exitSelect,
  });
  // 超限硬拦,不「截前 N 项照下」——静默部分下载是 said-not-did(toast 说打包了,实际丢一半)。
  const startBatchDownload = () => {
    const ids = [...selectedIds];
    if (ids.length > BATCH_DOWNLOAD_MAX) {
      toast({
        title: `一次最多打包 ${BATCH_DOWNLOAD_MAX} 项`,
        description: `已选 ${ids.length} 项,请取消部分选择后再下载`,
        variant: "destructive",
      });
      return;
    }
    batchDownloadMut.mutate(ids);
  };

  const openWork = (w: WorkItem) => {
    // 列表=预览:海报开图灯箱、视频开播放器、分身直接进详情(它的详情页本身就是播放页)
    if (w.line === "poster") setPreviewPoster(w);
    else if (w.line === "video") setPreviewVideoId(w.id);
    // OEM H1:新闻线未开通 → 不进改稿工作台(编辑面),进只读预览页(ProtectedRoute 对它放只读)
    else if (w.line === "jizhe" && isLocked("jizhe")) navigate(`/ai-jizhe/preview/${w.id}`);
    else navigate(w.detailRoute);
  };

  const handleDelete = async (item: WorkItem) => {
    const ok = await confirm({
      title: `删除「${item.title}」?`,
      description: "此操作不可撤销,连同该任务的产物一起删除。",
      confirmText: "删除",
      variant: "destructive",
    });
    if (ok) deleteMut.mutate(item);
  };
  const handleRetry = async (item: WorkItem) => {
    const ok = await confirm({
      title: "按原参数重新生成?",
      description: lineDefOf(item.line).retryConfirmDescription ?? "会消耗积分。",
      confirmText: "重新生成",
    });
    if (ok) retryMut.mutate(item);
  };

  /* ── 权限还没拉到时先转圈 ──
     ★不能直接落「你没有任何创作模块的权限」:/me/permissions 在测试环境实测要 4~6 秒才回,
     这期间 useModuleVisible 全 false —— 真按无权限渲染,用户看到的是一句**错的**结论
     (自测截图就撞上了这一幕)。权限未知 ≠ 无权限。 */
  if (!permissions && permissionsLoading) {
    return (
      <div className="w-full space-y-6">
        <PageHeader
          icon={FolderOpen}
          title="我的作品"
          subtitle="海报 / 视频 / 分身 / 新闻文章的全部产出"
        />
        <CenteredLoader />
      </div>
    );
  }

  /* ── 全线无权限:一句话 + 一个动作,不留白屏 ── */
  if (lines.length === 0) {
    return (
      <div className="w-full space-y-6">
        <PageHeader
          icon={FolderOpen}
          title="我的作品"
          subtitle="海报 / 视频 / 分身 / 新闻文章的全部产出"
        />
        <Card>
          <CardContent className="py-12 text-center">
            <p className="mb-4 text-muted-foreground">你还没有任何创作模块的权限。</p>
            <Button onClick={() => navigate("/")}>回工作台</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const sceneHub = activeLine === "all" ? null : lineDefOf(activeLine as WorkLineKey).hub;

  return (
    <div className="w-full space-y-6">
      <PageHeader
        icon={FolderOpen}
        title="我的作品"
        subtitle="四条线的产出都在这里 —— 在途、草稿、已完成,按部门 / 场景 / 状态筛。"
        actions={<OrgScopeFilterBar filter={orgFilter} />}
      />

      {/* 线 tabs:整页作用域(在途/草稿/作品都跟着切)→ 放页头之下、各节之上 */}
      <div
        role="tablist"
        aria-label="按部门筛选"
        data-testid="works-line-tabs"
        className="flex w-fit flex-wrap items-center gap-1 rounded-[calc(var(--radius)*0.75)] border bg-muted/40 p-1"
      >
        {[
          { key: "all" as const, label: "全部" },
          ...lines.map((l) => ({ key: l.key, label: l.label })),
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={activeLine === t.key}
            onClick={() => changeLine(t.key as WorkLineKey | "all")}
            className={cn(
              "rounded-[calc(var(--radius)*0.5)] px-3 py-1 text-xs transition-colors",
              activeLine === t.key
                ? "bg-background font-medium text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {failedLines.length > 0 && (
        <QueryErrorBanner
          isError
          message={`${failedLines.map((l) => l.label).join(" / ")} 这${
            failedLines.length > 1 ? "几" : ""
          }条线没加载出来,下面的列表少了它们的作品。`}
          onRetry={() => historyQs.forEach((q) => q.refetch())}
        />
      )}

      {/* ⓪ 已排程(海报定时出图)—— 空时组件自己不渲染;它是「要跑的」,与在途同族。
          只在海报线可见时挂:其余线没有排程概念,挂了也永远空。 */}
      {posterVisible && !favOnly && (activeLine === "all" || activeLine === "poster") && (
        <ScheduleListSection />
      )}

      {/* ① 在途 —— 独立拉 bucket=running,5s 轮询,不受筛选影响 */}
      {/* 只看收藏时收起在途/失败/草稿三节:它们不是收藏的对象,混在一起又成了「找不到」 */}
      {!favOnly && running.length > 0 && (
        <MediaListSection
          icon={Loader2}
          spin
          iconClassName="text-warning"
          title="进行中"
          count={running.length}
          testId="works-running-section"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {running.map((w) => (
              <MediaInProgressCard
                key={w.uid}
                name={w.title}
                statusLabel={workStatusText(w).label}
                statusHint={workStatusText(w).hint}
                createdAt={w.sortAt}
                creatorName={workCreatorName(w, user?.name)}
                originLabel={workOriginLabel(w)}
                onClick={() => (isLocked(w.line) ? notifyLocked(w.line) : navigate(w.detailRoute))}
              />
            ))}
          </div>
        </MediaListSection>
      )}

      {/* ①′ 近期没排成 —— 还没落成任务就挂了的作业(一键长图规划期失败,近 24h)。
          ★不许混在「进行中」里:那一节的卡和标题都在转圈(0920 测试机两张失败卡转圈挂 24h)。 */}
      {!favOnly && failedJobs.length > 0 && (
        <MediaListSection
          icon={AlertTriangle}
          iconClassName="text-destructive"
          title="最近没排成"
          count={failedJobs.length}
          testId="works-failed-jobs-section"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {failedJobs.map((w) => (
              <MediaFailedJobCard
                key={w.uid}
                name={w.title}
                reason={maskVendor(w.errorMessage)}
                createdAt={w.sortAt}
                onClick={() => (isLocked(w.line) ? notifyLocked(w.line) : navigate(w.detailRoute))}
              />
            ))}
          </div>
        </MediaListSection>
      )}

      {/* ② 草稿 —— 已编排未生成,点继续编辑恢复表单 */}
      {!favOnly && drafts.length > 0 && (
        <MediaListSection
          icon={FilePenLine}
          iconClassName="text-info"
          title="我的草稿"
          count={drafts.length}
          className="work-row"
        >
          <div className="work-grid">
            {drafts.map((d) => (
              <MediaDraftCard
                key={d.uid}
                name={d.name}
                coverUrl={d.coverUrl}
                /* 有「编排中 / 编排失败」这类状态就先说状态,没有才退回舱名 */
                hint={d.statusHint ?? d.lineLabel}
                busy={d.busy}
                updatedAt={d.updatedAt}
                // OEM H1:继续编辑草稿 = 新建(会消耗积分)→ 未开通只提示;删除照常
                onOpen={() =>
                  isLocked(d.line)
                    ? notifyLocked(d.line)
                    : d.busy && d.line === "poster"
                      ? notifyPlanBusy()
                      : navigate(d.resumeRoute)
                }
                onGoProduced={
                  d.producedRoute && !isLocked(d.line)
                    ? () => navigate(d.producedRoute!)
                    : undefined
                }
                placeholderIcon={FilePenLine}
                onDelete={(e) => {
                  e.stopPropagation();
                  deleteDraftMut.mutate({ line: d.line, draftId: d.draftId });
                }}
              />
            ))}
          </div>
        </MediaListSection>
      )}

      {/* ③ 作品(终态)—— 筛选 chips 只作用于本节,故挂在本节标题行 */}
      <MediaListSection
        icon={FolderOpen}
        iconClassName="text-success"
        title="作品"
        count={totalAll}
        className="work-row"
        actions={
          <>
            {/* ★批量打包下载只在海报线出现:后端只有海报有 zip 端点。
                跨线多选 = 选了视频却下不出来,是个假承诺。 */}
            {activeLine === "poster" && (
              <Button
                variant={selectMode ? "secondary" : "outline"}
                size="sm"
                className="h-8 gap-1.5 text-xs"
                onClick={() => (selectMode ? exitSelect() : setSelectMode(true))}
              >
                <Download className="h-3.5 w-3.5" />
                {selectMode ? "退出多选" : "批量下载"}
              </Button>
            )}
            {/* ★「只看我的」只在新闻线出现、且**对所有角色**渲染(E2E 0810 RP-WK-01):
                其余三线 STAFF 由后端强制 own,不需要开关;新闻线是租户级轻列表、无 own 强制,
                而页头 OrgScopeFilterBar 只给 MANAGER+ 渲染 → STAFF 此前没有任何入口收窄到
                自己的稿子。管理角色也照渲染:与页头树 picker 共用同一份 orgFilter URL 状态,
                两个控件天然同步,不会各说各话。 */}
            {activeLine === "jizhe" && (
              <Button
                variant={orgFilter.scope === "mine" ? "secondary" : "outline"}
                size="sm"
                className="h-8 gap-1.5 text-xs"
                aria-pressed={orgFilter.scope === "mine"}
                onClick={() =>
                  orgFilter.scope === "mine" ? orgFilter.selectAll() : orgFilter.selectMine()
                }
              >
                <UserRound className="h-3.5 w-3.5" />
                只看我的
              </Button>
            )}
            {/* 1102 只看收藏:新闻线没有收藏,不出按钮 */}
            {activeLine !== "jizhe" && (
              <Button
                variant={favOnly ? "secondary" : "outline"}
                size="sm"
                className="h-8 gap-1.5 text-xs"
                aria-pressed={favOnly}
                onClick={toggleFav}
                data-testid="works-fav-toggle"
              >
                <Star
                  className={favOnly ? "h-3.5 w-3.5 fill-warning text-warning" : "h-3.5 w-3.5"}
                />
                我的收藏
              </Button>
            )}
            <MediaFilterChips value={statusFilter} onChange={changeStatus} />
            {/* 场景 chips 只在选定某条线时出现:sceneKey 按 hub 分档,「全部」下把三条线
                二十多个场景铺一排既读不完、也筛不出跨线结果 */}
            {sceneHub && (
              <SceneFilterChips hub={sceneHub} value={sceneFilter} onChange={changeScene} />
            )}
            <MediaSearchBox value={search} onChange={changeSearch} placeholder="搜索作品名称" />
          </>
        }
      >
        {historyLoading && merged.length === 0 && <CenteredLoader />}

        {!historyLoading && merged.length === 0 && failedLines.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              {favOnly && statusFilter === "ALL" && !search.trim() && !sceneFilter ? (
                <p className="inline-flex items-center gap-2 text-muted-foreground">
                  <Star className="h-4 w-4" />
                  你还没有收藏作品,在作品卡上点星标就能收藏。
                </p>
              ) : statusFilter === "ALL" && !search.trim() && !sceneFilter && !favOnly ? (
                <>
                  <p className="mb-4 text-muted-foreground">还没有作品。</p>
                  {/* ★空态 CTA 指向**当前选中的线**,不是恒定的 lines[0]:codex review P2 ——
                      多模块用户打开空的非首 tab(如 line=video)时,原来按钮写「去创意设计中心」
                      并跳海报 hub,指错了部门。queriedLines 是「当前 tab 实际查的线」,取它的头。 */}
                  {(() => {
                    const target = queriedLines[0] ?? lines[0];
                    // OEM H1:未开通的线不给「去 XX」创作入口
                    if (isLocked(target.key)) {
                      return (
                        <p className="text-xs text-muted-foreground">
                          {lockedNoticeOf(target.key)}
                        </p>
                      );
                    }
                    return (
                      <Button onClick={() => navigate(target.hubRoute)}>去{target.label}</Button>
                    );
                  })()}
                </>
              ) : (
                <p className="inline-flex items-center gap-2 text-muted-foreground">
                  <Search className="h-4 w-4" />
                  没有符合当前筛选的作品。
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {merged.length > 0 && (
          <div className="work-grid">
            {merged.map((w) => {
              const def = lineDefOf(w.line);
              return (
                <WorkCard
                  key={w.uid}
                  item={w}
                  onOpen={() => openWork(w)}
                  onRename={def.rename ? (name) => renameMut.mutate({ item: w, name }) : undefined}
                  onToggleFavorite={def.setFavorite ? () => favMut.mutate(w) : undefined}
                  onDelete={def.remove ? () => void handleDelete(w) : undefined}
                  // OEM H1:重新生成 = 消耗积分的新建动作 → 未开通不出按钮,卡上挂只读提示
                  // 分身被审核拦下(BLOCKED)按原参数重试多半再被拦、又扣一次 → 不出重试
                  // 1019:照片省流版已下架,api 对它的重试一律 400 → 不出重试,失败卡上写明「该档已下架」
                  onRetry={
                    def.retry &&
                    !isLocked(w.line) &&
                    !(w.line === "dh" && w.status === "BLOCKED") &&
                    !dhRetired(w)
                      ? () => void handleRetry(w)
                      : undefined
                  }
                  lockedNotice={
                    isLocked(w.line)
                      ? lockedNoticeOf(w.line)
                      : dhRetired(w) && w.status !== "SUCCESS"
                        ? DH_RETIRED_MODE_NOTICE
                        : undefined
                  }
                  footnote={
                    w.line === "jizhe" && (siteBadgesQ.isSuccess || tenantHasSites) ? (
                      <SiteBadgeLine
                        items={siteBadgeByDraft.get(String(w.id))}
                        hasSites={tenantHasSites}
                      />
                    ) : undefined
                  }
                  deleting={deleteMut.isPending && deleteMut.variables?.uid === w.uid}
                  retrying={retryMut.isPending && retryMut.variables?.uid === w.uid}
                  selected={selectMode ? selectedIds.has(w.id) : undefined}
                  onSelectToggle={
                    selectMode
                      ? () =>
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (next.has(w.id)) next.delete(w.id);
                            else next.add(w.id);
                            return next;
                          })
                      : undefined
                  }
                />
              );
            })}
          </div>
        )}

        {/* 多选工具条:贴底常驻,选中数与上限一起显示(不等到点下载才告诉你超了)。
            ★双保险 activeLine==="poster":batch 是海报独有,切线已 exitSelect,这里再挡一层。 */}
        {selectMode && activeLine === "poster" && (
          <div className="sticky bottom-4 z-20 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-[calc(var(--radius)*1.5)] border bg-card/95 px-4 py-3 shadow-lg backdrop-blur">
            <span className="text-sm text-muted-foreground">
              已选 <b className="text-foreground">{selectedIds.size}</b>/{BATCH_DOWNLOAD_MAX} 个任务
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // 全选钳到打包上限:当前列表远多于上限,整页塞进去必被硬拦,不如选满即止
                  setSelectedIds(new Set(clampBatchIds(merged.map((w) => w.id))));
                  if (merged.length > BATCH_DOWNLOAD_MAX) {
                    toast({
                      title: `已选中前 ${BATCH_DOWNLOAD_MAX} 项`,
                      description: `单次打包上限 ${BATCH_DOWNLOAD_MAX} 项,超出部分未选中`,
                    });
                  }
                }}
              >
                全选
              </Button>
              <Button variant="ghost" size="sm" onClick={exitSelect}>
                取消
              </Button>
              <Button
                size="sm"
                className="gap-1.5"
                disabled={selectedIds.size === 0 || batchDownloadMut.isPending}
                onClick={startBatchDownload}
              >
                {batchDownloadMut.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
                打包下载
              </Button>
            </div>
          </div>
        )}

        {/* 单线 = 单一服务端分页 → 共享分页器真翻页;「全部」= k 路归并 → 只能「加载更多」(文件头★) */}
        {singleLine && totalAll > PAGE && (
          <Pagination
            className="mt-6"
            page={pageN}
            pageSize={PAGE}
            total={totalAll}
            onPageChange={changePage}
            disabled={historyFetching}
          />
        )}
        {(hasMore || cappedRemaining > 0) && (
          <div className="mt-6 flex flex-col items-center gap-2">
            {hasMore && (
              <Button variant="outline" onClick={() => setPages((p) => p + 1)}>
                加载更多
              </Button>
            )}
            <span className="text-xs text-muted-foreground">
              已显示 {merged.length} / 共 {totalAll} 件
              {/* ★不静默截断:到顶了就说清还有多少条没显示,以及怎么看到它们 */}
              {cappedRemaining > 0 && ` · 还有 ${cappedRemaining} 件,请用筛选缩小范围`}
            </span>
          </div>
        )}
      </MediaListSection>

      {/* 列表=预览,详情=编辑:海报开图灯箱、视频开播放器(两者都是各 feature 既有件,不另造) */}
      <ImagePreviewLightbox
        taskId={previewPoster?.id ?? null}
        coverUrl={previewPoster?.coverUrl ?? null}
        onClose={() => setPreviewPoster(null)}
        onEnterDetail={(id) => navigate(`/ai-haibao/tasks/${id}`)}
        // OEM H1:详情=编辑面(再来一张 / 改图 / 让它动起来),未开通只留预览与下载
        lockedNotice={isLocked("poster") ? lockedNoticeOf("poster") : undefined}
      />
      <VideoPreviewDialog
        taskId={previewVideoId}
        onClose={() => setPreviewVideoId(null)}
        onEnterDetail={(id) => navigate(`/ai-video/tasks/${id}`)}
        lockedNotice={isLocked("video") ? lockedNoticeOf("video") : undefined}
      />
    </div>
  );
}

/** 1019:分身线上已下架档位(照片省流版)的历史作品 —— api 不再接它的重试。 */
function dhRetired(w: WorkItem): boolean {
  return w.line === "dh" && isRetiredDhMode(w.variant);
}

function lineDefOf(key: WorkLineKey): WorkLineDef {
  const l = WORK_LINES.find((x) => x.key === key);
  if (!l) throw new Error(`unknown work line: ${key}`);
  return l;
}
